import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import { Modal } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import MediaCropper from '@/components/cropper';
import Icon from '@/components/icon';
import Button from '@/components/overrides/button';
import { uploadToS3 } from '@/utils/imageUtils';

import type { MatchFlowSource } from '.';

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('The cropped image could not be created.')),
      'image/jpeg',
      0.92
    );
  });

interface UploadMatchProps {
  onReady: (source: MatchFlowSource) => void;
}

function UploadMatch({ onReady }: UploadMatchProps) {
  const [open, setOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [original, setOriginal] = useState('');
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const chooseFile = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const image = String(reader.result);
      setOriginal(image);
      setPreview(image);
      setError('');
      setCropOpen(true);
    };
    reader.onerror = () => setError('The selected image could not be read.');
    reader.readAsDataURL(file);
  };

  const saveCrop = async (canvas: HTMLCanvasElement) => {
    if (saving) return;
    try {
      setSaving(true);
      setError('');
      const blob = await canvasToBlob(canvas);
      const id = uuidv4();
      const upload = await uploadToS3(
        blob,
        'cigars/match-debugger',
        `${id}.jpg`,
        id
      );
      const croppedUrl = upload.data.media_url;
      setPreview(croppedUrl);
      setCropOpen(false);
      setOpen(false);
      onReady({
        image: croppedUrl,
        originalImage: original,
        sourceType: 'upload',
        sourceId: id,
      });
    } catch (uploadError: any) {
      setError(
        uploadError?.response?.data?.message ||
          uploadError?.message ||
          'The cropped image could not be prepared.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={() => setOpen(true)}
      >
        <Icon name="upload-cloud" size={14} />
        Upload &amp; Match
      </button>
      <Modal
        isOpen={open}
        toggle={() => setOpen(false)}
        className="modal-dialog-lg"
      >
        <div className="modal-header">
          <div>
            <h5 className="modal-title">Upload &amp; Match</h5>
            <small className="text-muted">
              Choose an image, make a freeform crop, then launch the recognition
              debugger.
            </small>
          </div>
        </div>
        <div className="modal-body">
          <Dropzone
            accept={{
              'image/jpeg': ['.jpg', '.jpeg'],
              'image/png': ['.png'],
              'image/webp': ['.webp'],
            }}
            multiple={false}
            onDrop={chooseFile}
            onDropRejected={() =>
              setError('Choose one JPG, PNG, or WebP image.')
            }
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                className={`recognition-dropzone${isDragActive ? ' active' : ''}`}
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <Icon name="upload-cloud" size={30} />
                <strong>
                  {isDragActive
                    ? 'Drop the image here'
                    : 'Drop an image or click to browse'}
                </strong>
                <span>Freeform cropping opens next</span>
              </div>
            )}
          </Dropzone>
          {preview && (
            <div className="mt-3 text-center">
              <img
                src={preview}
                alt="Upload preview"
                style={{ maxWidth: '100%', maxHeight: 280 }}
              />
            </div>
          )}
          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </div>
        <div className="modal-footer">
          <Button color="light" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {original && (
            <Button color="primary" onClick={() => setCropOpen(true)}>
              Crop again
            </Button>
          )}
        </div>
      </Modal>
      <MediaCropper
        src={original}
        open={cropOpen}
        toggle={() => !saving && setCropOpen(false)}
        onClose={() => !saving && setCropOpen(false)}
        onSave={saveCrop}
        saving={saving}
      />
    </>
  );
}

export default UploadMatch;
